import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName } from '@/components';
import HeaderMenu from '@/components/HeaderMenu';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const isDevOrTest = isDev || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      // 从localStorage获取用户信息
      const userInfoStr = localStorage.getItem('userInfo');
      const token = localStorage.getItem('token');
      console.log('fetchUserInfo', userInfoStr, token);

      if (userInfoStr && token) {
        const userInfo = JSON.parse(userInfoStr);
        // 将我们的用户信息格式转换为API.CurrentUser格式
        return {
          name: userInfo.userName,
          avatar: '/images/header.jpg', // 如果有头像字段可以添加
          userid: userInfo.userId,
          email: '', // 如果有邮箱字段可以添加
          signature: '',
          title: '',
          group: '',
          tags: [],
          notifyCount: 0,
          unreadCount: 0,
          country: '',
          access: '',
          geographic: {
            province: { label: '', key: '' },
            city: { label: '', key: '' },
          },
          address: '',
          phone: userInfo.mobilePhone,
          projectId: userInfo.projectId,
        };
      }

      return undefined;
    } catch (_error) {
      console.log('fetchUserInfo', _error);
      // 清除可能损坏的数据
      localStorage.removeItem('userInfo');
      localStorage.removeItem('token');
      localStorage.removeItem('permissions');
      localStorage.removeItem('menus');
    }
    return undefined;
  };

  console.log('history', history);
  // 检查当前路径
  const { location } = history;
  const isLoginPage = [loginPath, '/user/register', '/user/register-result'].includes(location.pathname);

  console.log('getInitialState - 当前路径:', location.pathname, '是否登录页:', isLoginPage);

  // 如果不是登录页面，检查用户信息
  if (!isLoginPage) {
    const currentUser = await fetchUserInfo();

    console.log('getInitialState - 获取到的用户信息:', currentUser);

    // 如果没有用户信息，跳转到登录页
    if (!currentUser) {
      console.log('getInitialState - 没有用户信息，跳转到登录页');
      history.push(loginPath);
      return {
        fetchUserInfo,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }

    console.log('getInitialState - 有用户信息，正常返回');
    // 有用户信息，正常返回
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  console.log('getInitialState - 登录页面，直接返回');
  // 登录页面，直接返回
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    actionsRender: () => [
      // <Question key="doc" />,
      // <SelectLang key="SelectLang" />,
      React.createElement(HeaderMenu, { key: 'HeaderMenu' }),
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => (
        <AvatarDropdown>{avatarChildren}</AvatarDropdown>
      ),
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDevOrTest
      ? [
          // <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          //   <LinkOutlined />
          //   <span>OpenAPI 文档</span>
          // </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {/*{isDevOrTest && (*/}
          {/*  <SettingDrawer*/}
          {/*    disableUrlParams*/}
          {/*    enableDarkTheme*/}
          {/*    settings={initialState?.settings}*/}
          {/*    onSettingChange={(settings) => {*/}
          {/*      setInitialState((preInitialState) => ({*/}
          {/*        ...preInitialState,*/}
          {/*        settings,*/}
          {/*      }));*/}
          {/*    }}*/}
          {/*  />*/}
          {/*)}*/}
        </>
      );
    },
    contentStyle: { padding: 0 }, // 去掉 ant-pro-layout-content 的 padding
    ...initialState?.settings,
    logo: '/images/logo_ruidan.png',
    headerHeight: 48,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: isDev ? '' : 'https://proapi.azurewebsites.net',
  ...errorConfig,
};
